import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense.ts";
import { ReturnRequestLogDto } from "@babackapp/private-api-client/types";
import useTranslation from "@babackapp/translations/use-translation";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@babackapp/ui/components/accordion";
import { Badge } from "@babackapp/ui/components/badge";
import { EditIcon } from "lucide-react";
import ActivityContentWrapper from "./activity-content-wrapper";
import ActivityIconWrapper from "./activity-icon-wrapper";
import { ActivityItemHeader } from "./activity-item-header";
import ActivityWrapper from "./activity-wrapper";
import { getAuthorName } from "@/pages/(app)/returns/utils/get-author-name.ts";

type LineItemModificationProps = {
  activity: ReturnRequestLogDto;
};

type LineItemModificationParams = {
  lineItemId: string;
  title: string;
  changes: Array<{
    type: string;
    oldValue: string;
    newValue: string;
  }>;
};

export default function LineItemModificationActivity({
  activity,
}: LineItemModificationProps) {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();

  const author = shop.users.find(
    (user) => user.userId === activity.author,
  )?.user;
  const authorName = getAuthorName(author);

  const params = activity.params as unknown as LineItemModificationParams;

  const getChangeLabel = (changeType: string) => {
    switch (changeType) {
      case "resolution":
        return t("returnDetail.resolution");
      case "quantity":
        return t("returnDetail.quantity");
      case "reason":
        return t("returnDetail.reason");
      default:
        return changeType;
    }
  };

  const getValueLabel = (value: string) => {
    if (!value || value.trim() === "") {
      return "—";
    }

    switch (value) {
      case "exchange":
        return t("returnDetail.exchange");
      case "discount":
        return t("returnDetail.discount");
      case "refund":
        return t("returnDetail.refund");
      default:
        return value;
    }
  };

  return (
    <ActivityWrapper>
      <ActivityIconWrapper>
        <EditIcon className="size-4" />
      </ActivityIconWrapper>
      <ActivityContentWrapper>
        <div className="flex items-center gap-2 flex-1 justify-between w-full">
          <Accordion type="single" collapsible className="flex-1">
            <AccordionItem value="item-1" className="border-0">
              <AccordionTrigger className="p-0 w-full">
                <ActivityItemHeader title="" timestamp={activity.date}>
                  <span className="font-bold">{authorName}</span>
                  {t("returnDetail.modifiedItem")} "{params.title}"
                </ActivityItemHeader>
              </AccordionTrigger>
              <AccordionContent>
                <div className="mt-2 space-y-1">
                  {params.changes.map((change, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-2 text-xs"
                    >
                      <span className="text-muted-foreground">
                        {getChangeLabel(change.type)}:
                      </span>
                      <Badge variant="outline" className="text-xs">
                        {getValueLabel(change.oldValue)}
                      </Badge>
                      <span className="text-muted-foreground">→</span>
                      <Badge variant="outline" className="text-xs">
                        {getValueLabel(change.newValue)}
                      </Badge>
                    </div>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </ActivityContentWrapper>
    </ActivityWrapper>
  );
}
